// src/feature-module/subscription/SubscriptionRenewal.jsx
import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Card,
  Row,
  Col,
  Tag,
  Space,
  Button,
  Switch,
  Alert,
  Typography,
  Descriptions,
  Progress,
  List,
  Skeleton,
  Modal,
  Drawer,
  Table,
  message,
  Result,
  Divider,
  Empty,
  Segmented,
  Input,
  Form,
  InputNumber,
  Grid,
} from "antd";
import {
  ThunderboltOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DollarCircleOutlined,
  SyncOutlined,
  StopOutlined,
  CrownOutlined,
  ArrowRightOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

import {
  getLatestFeatureSubscription,
  previewNextFeatureRenewal,
  setFeatureAutoRenew,
  cancelFeatureSubscription,
  startFeatureSubscriptionWithProvider,
  listPosInventoryPackages,
} from "../../api/subscriptionApi";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// simple alias so component won't crash if window.translate is missing
const t = (s) =>
  typeof window !== "undefined" && typeof window.translate === "function"
    ? window.translate(s)
    : s;

function prettyMoney(v, ccy = "KWD") {
  if (v == null) return "—";
  const n = Number(v);
  return `${ccy} ${n.toLocaleString()}`;
}
function parsePairId(id = "") {
  const [providerPackageId, providerPackageOptionId] = String(id).split(":");
  return { providerPackageId, providerPackageOptionId };
}

/* ---------- UI atoms ---------- */
function LimitPill({ label, value }) {
  if (value == null) return null;
  return (
    <Tag style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12 }}>
      {t(label)}: <b>{value}</b>
    </Tag>
  );
}
LimitPill.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function UsageBar({ used, total, label }) {
  if (!total) return null;
  const pct = Math.min(100, Math.round(((used || 0) / total) * 100));
  const status = pct >= 95 ? "exception" : pct >= 75 ? "active" : "normal";
  return (
    <div style={{ marginBottom: 12 }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Text type="secondary">{t(label)}</Text>
        <Text strong>
          {used || 0} / {total}
        </Text>
      </Space>
      <Progress percent={pct} status={status} showInfo />
    </div>
  );
}
UsageBar.propTypes = {
  used: PropTypes.number,
  total: PropTypes.number,
  label: PropTypes.string.isRequired,
};
UsageBar.defaultProps = { used: 0, total: 0 };

/* ---------- Complex hero ---------- */
const LimitsShape = PropTypes.shape({
  productLimit: PropTypes.number,
  userAccessLimit: PropTypes.number,
  storeLimit: PropTypes.number,
  warehouseLimit: PropTypes.number,
});
const PackageOptionShape = PropTypes.shape({ limits: LimitsShape });
const SubShape = PropTypes.shape({
  packageName: PropTypes.string,
  interval: PropTypes.string,
  currency: PropTypes.string,
  tapTransactionId: PropTypes.string,
  packageOption: PackageOptionShape,
});
const PreviewShape = PropTypes.shape({
  amount: PropTypes.number,
  currency: PropTypes.string,
  nextBillingAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  usage: PropTypes.shape({ productCount: PropTypes.number }),
});

//autoRenew, autoRenewSaving, onToggleAutoRenew <- add this in function parameters if you re-enable auto-renew toggle
function HeroCard({ sub, preview, active, daysLeft }) {
  return (
    <Card
      bordered={false}
      style={{
        background:
          "linear-gradient(135deg, #1677FF 0%, #3F87F5 30%, #6C8CF2 60%, #A1B4FF 100%)",
        color: "#fff",
        boxShadow: "0 8px 30px rgba(22,119,255,0.25)",
        borderRadius: 16,
      }}
      bodyStyle={{ padding: 24 }}
    >
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} md={14}>
          <Space direction="vertical" size={6}>
            <Title level={3} style={{ color: "#fff", margin: 0 }}>
              <Space>
                <CrownOutlined /> {sub.packageName}
              </Space>
            </Title>
            <Text style={{ color: "#E6F4FF" }}>
              {(sub.interval || "yearly").toUpperCase()} · {t("Next billing")}{" "}
              {preview?.nextBillingAt
                ? dayjs(preview.nextBillingAt).format("DD MMM YYYY")
                : "—"}
            </Text>
            <Space wrap>
              <Tag
                color={active ? "green" : "red"}
                icon={<CheckCircleOutlined />}
              >
                {active ? t("Active") : t("Inactive")}
              </Tag>
              {Number.isFinite(daysLeft) && (
                <Tag color="gold">
                  {daysLeft} {t("days left")}
                </Tag>
              )}
            </Space>
          </Space>
        </Col>

        <Col xs={24} md={10} style={{ textAlign: "right" }}>
          <Space direction="vertical" align="end">
            <Text style={{ color: "#E6F4FF" }}>{t("Billed amount")}</Text>
            <Title level={2} style={{ color: "#fff", margin: 0 }}>
              {prettyMoney(preview?.amount, preview?.currency || sub.currency)}
            </Title>

            {/* 🔽 Auto-renew toggle lives here */}
            {/* <Space align="center" style={{ background: "rgba(255,255,255,0.15)", padding: "6px 10px", borderRadius: 999 }}>
              <Text style={{ color: "#fff" }}>{t("Auto-renew")}</Text>
              <Switch
                checked={autoRenew}
                loading={autoRenewSaving}
                onChange={onToggleAutoRenew}
              />
            </Space> */}

            <Space wrap>
              <LimitPill
                label="Products"
                value={sub?.packageOption?.limits?.productLimit}
              />
              <LimitPill
                label="Users"
                value={sub?.packageOption?.limits?.userAccessLimit}
              />
              <LimitPill
                label="Stores"
                value={sub?.packageOption?.limits?.storeLimit}
              />
              <LimitPill
                label="Warehouses"
                value={sub?.packageOption?.limits?.warehouseLimit}
              />
            </Space>
          </Space>
        </Col>
      </Row>
    </Card>
  );
}

HeroCard.propTypes = {
  sub: SubShape.isRequired,
  preview: PreviewShape,
  active: PropTypes.bool,
  daysLeft: PropTypes.number,
  autoRenew: PropTypes.bool,
  autoRenewSaving: PropTypes.bool,
  onToggleAutoRenew: PropTypes.func.isRequired,
};

/* ---------- Main ---------- */
export default function SubscriptionRenewal() {
  const screens = useBreakpoint();

  const pageStyle = React.useMemo(
    () => ({
      maxWidth: 1280,
      margin: "0 auto",
      padding: screens.xs ? 12 : 16,
    }),
    [screens.xs]
  );

  const [creds, setCreds] = React.useState({
    storeId: null,
    email: "",
    password: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [sub, setSub] = React.useState(null);
  const [active, setActive] = React.useState(false);
  const [daysLeft, setDaysLeft] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [autoRenew, setAutoRenew] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState([]);
  const [pkgDrawerOpen, setPkgDrawerOpen] = React.useState(false);
  const [pkgRows, setPkgRows] = React.useState([]);
  const [selecting, setSelecting] = React.useState(false);
  const [autoRenewSaving, setAutoRenewSaving] = React.useState(false);

  const [intervalFilter, setIntervalFilter] = React.useState("All");
  const [planFilter, setPlanFilter] = React.useState("All");
  const [search, setSearch] = React.useState("");
  const [form] = Form.useForm();

  const features = React.useMemo(() => ["website", "pos", "inventory"], []);
  const canRun = React.useMemo(
    () => Boolean(creds.storeId) && Boolean(creds.email),
    [creds.storeId, creds.email]
  );

  const load = React.useCallback(
    async (params) => {
      const storeId = params?.storeId ?? creds.storeId;
      const email = params?.email ?? creds.email;
      const localCanRun = Boolean(storeId) && Boolean(email);
      if (!localCanRun) return;

      setLoading(true);
      try {
        const latest = await getLatestFeatureSubscription({
          storeId,
          email,
          features,
        });
        setSub(latest.subscription);
        setActive(Boolean(latest.isActive));
        setDaysLeft(latest.daysLeft);
        setSuggestions(latest.suggestions || []);
        setAutoRenew(Boolean(latest.subscription?.autoRenew));
      } catch (e) {
        const status = e?.response?.status;
        message.warning(
          status === 404
            ? t("No active subscription.")
            : e?.response?.data?.error ||
                e.message ||
                t("Failed to load subscription")
        );
        setSub(null);
        setLoading(false);
        return;
      }

      try {
        const prev = await previewNextFeatureRenewal({
          storeId,
          email,
          features,
        });
        setPreview(prev);
      } catch (e) {
        console.warn(
          "preview-next-feature-renewal failed:",
          e?.response?.data || e.message
        );
        setPreview(null);
      } finally {
        setLoading(false);
      }
    },
    [creds.storeId, creds.email, features]
  );

  const onSubmitCreds = (vals) => {
    const next = {
      storeId: vals.storeId,
      email: vals.email?.trim(),
      password: vals.password || "",
    };
    setCreds(next);
    load(next);
  };

  async function onToggleAutoRenew(checked) {
    if (!sub) return;
    try {
      setAutoRenewSaving(true);
      setAutoRenew(checked); // optimistic update

      await setFeatureAutoRenew({
        storeId: creds.storeId,
        email: creds.email,
        features,
        enable: checked,
        password: creds.password || "", // pass through if you use it
      });

      // keep local sub in sync so UI stays consistent
      setSub((prev) => (prev ? { ...prev, autoRenew: checked } : prev));
      message.success(
        checked ? t("Auto-renew enabled") : t("Auto-renew disabled")
      );
    } catch (e) {
      setAutoRenew((prev) => !prev); // revert on error
      message.error(
        e?.response?.data?.error || t("Failed to update auto-renew")
      );
    } finally {
      setAutoRenewSaving(false);
    }
  }

  async function onCancel() {
    Modal.confirm({
      title: t("Cancel subscription?"),
      icon: <ExclamationCircleOutlined />,
      content: t("Access stays active until the current period ends."),
      okText: t("Cancel Subscription"),
      okType: "danger",
      onOk: async () => {
        try {
          await cancelFeatureSubscription({
            storeId: creds.storeId,
            email: creds.email,
            features,
          });
          message.success(t("Subscription will not renew."));
          await load();
        } catch (e) {
          message.error(e?.response?.data?.error || t("Cancel failed"));
        }
      },
    });
  }

  async function openUpgradeDrawer() {
    setPkgDrawerOpen(true);
    try {
      const rows = await listPosInventoryPackages();
      setPkgRows(rows);
    } catch (e) {
      message.error(t("Failed to load packages"));
    }
  }

  async function goUpgradeFromSuggestion(sugg) {
    const { providerPackageId, providerPackageOptionId } = parsePairId(sugg.id);
    await beginCheckout({ providerPackageId, providerPackageOptionId });
  }

  async function beginCheckout({ providerPackageId, providerPackageOptionId }) {
    try {
      setSelecting(true);
      const r = await startFeatureSubscriptionWithProvider({
        storeId: creds.storeId,
        email: creds.email,
        providerPackageId,
        providerPackageOptionId,
        features,
        autoRenew: true,
      });
      if (r?.url) window.location.href = r.url;
      else message.error(t("Failed to start checkout"));
    } catch (e) {
      message.error(
        e?.response?.data?.error || e.message || t("Checkout failed")
      );
    } finally {
      setSelecting(false);
    }
  }

  const filteredRows = React.useMemo(() => {
    let rows = pkgRows.slice();
    if (intervalFilter !== "All")
      rows = rows.filter(
        (r) =>
          (r.pkg.subscription_type || "yearly").toLowerCase() ===
          intervalFilter.toLowerCase()
      );
    if (planFilter !== "All")
      rows = rows.filter((r) =>
        (r.opt?.name_en || "Base")
          .toLowerCase()
          .includes(planFilter.toLowerCase())
      );
    if (search.trim())
      rows = rows.filter((r) =>
        r.searchText.includes(search.trim().toLowerCase())
      );
    rows.sort((a, b) => a.discounted - b.discounted);
    return rows;
  }, [pkgRows, intervalFilter, planFilter, search]);

  const pkgColumns = [
    {
      title: t("Package"),
      dataIndex: ["pkg", "name_en"],
      key: "name",
      render: (v, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.pkg.name_en}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.opt?.name_en || "Base"}
          </Text>
        </Space>
      ),
    },
    {
      title: t("Limits"),
      key: "limits",
      render: (_, row) => {
        const val = row.opt?.value_en || "";
        return <Text>{val || "—"}</Text>;
      },
    },
    {
      title: t("Discounted Price"),
      key: "price",
      render: (_, row) => (
        <Tag
          color="blue"
          style={{ fontSize: 14, padding: "6px 10px", borderRadius: 16 }}
        >
          {prettyMoney(row.discounted)}
        </Tag>
      ),
    },
    {
      title: t("Action"),
      key: "action",
      fixed: "right",
      render: (_, row) => (
        <Button
          type="primary"
          loading={selecting}
          onClick={() =>
            beginCheckout({
              providerPackageId: row.pkg.id,
              providerPackageOptionId: row.opt?.id,
            })
          }
        >
          {t("Choose")}
        </Button>
      ),
    },
  ];

  const drawerWidth = screens.xl
    ? 980
    : screens.lg
    ? 860
    : screens.md
    ? 720
    : "100%";

  return (
    <div style={pageStyle}>
      {/* Header */}
      <Space wrap>
        <Text type="secondary">{t("Auto-renew")}</Text>
        <Switch
          checked={autoRenew}
          loading={autoRenewSaving}
          disabled={!sub}
          onChange={onToggleAutoRenew}
        />
        <Button
          danger
          icon={<StopOutlined />}
          onClick={onCancel}
          disabled={autoRenewSaving} // optional: prevent collision while saving
        >
          {t("Cancel")}
        </Button>
      </Space>

      {/* Credentials form */}
      <Card
        bordered
        style={{ marginBottom: 16, borderRadius: 12 }}
        headStyle={{ padding: screens.xs ? "12px 16px" : "16px 20px" }}
        title={
          <Space>
            <SafetyCertificateOutlined />
            {t("Connect Subscription")}
          </Space>
        }
        extra={
          <Space.Compact>
            <Button onClick={() => form.resetFields()}>{t("Clear")}</Button>
            <Button type="primary" onClick={() => form.submit()}>
              {t("Load")}
            </Button>
          </Space.Compact>
        }
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          initialValues={{
            storeId: creds.storeId,
            email: creds.email,
            password: "",
          }}
          onFinish={onSubmitCreds}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t("Store ID")}
                name="storeId"
                rules={[{ required: true, message: t("Store ID is required") }]}
              >
                <InputNumber
                  size="large"
                  style={{ width: "100%" }}
                  placeholder="e.g., 455564"
                  controls={false}
                  min={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t("Email")}
                name="email"
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: t("Valid email required"),
                  },
                ]}
              >
                <Input size="large" placeholder="you@domain.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t("Password (optional)")} name="password">
                <Input.Password
                  size="large"
                  placeholder={t("Only if your backend requires")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Alert
            style={{ borderRadius: 8 }}
            type="info"
            showIcon
            message={t("We don't store credentials in the browser.")}
            description={t(
              "They’re used only to fetch your subscription from the backend."
            )}
          />
        </Form>
      </Card>

      {/* Content */}
      {!canRun ? (
        <Result
          icon={<ExclamationCircleOutlined />}
          title={t("Enter Store ID and Email to view your subscription")}
          extra={
            <Button type="primary" onClick={() => form.submit()}>
              {t("Load Subscription")}
            </Button>
          }
        />
      ) : loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : !sub ? (
        <Result
          status="404"
          title={t("No subscription found")}
          subTitle={t(
            "Start a POS + Inventory subscription to unlock features."
          )}
          extra={
            <Button type="primary" onClick={openUpgradeDrawer}>
              {t("View Packages")}
            </Button>
          }
        />
      ) : (
        <>
          <HeroCard
            sub={sub}
            preview={preview}
            active={active}
            daysLeft={daysLeft}
            autoRenew={autoRenew}
            autoRenewSaving={autoRenewSaving}
            onToggleAutoRenew={onToggleAutoRenew}
          />

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={16}>
              <Card
                title={
                  <Space>
                    <DollarCircleOutlined /> {t("Details")}
                  </Space>
                }
                bordered
                style={{ borderRadius: 12 }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Descriptions
                      column={1}
                      size="small"
                      labelStyle={{ width: 140 }}
                    >
                      <Descriptions.Item label={t("Interval")}>
                        {(sub.interval || "yearly").toUpperCase()}
                      </Descriptions.Item>
                      <Descriptions.Item label={t("Next billing")}>
                        {preview?.nextBillingAt
                          ? dayjs(preview.nextBillingAt).format("DD MMM YYYY")
                          : "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label={t("Billed amount")}>
                        {prettyMoney(
                          preview?.amount,
                          preview?.currency || sub.currency
                        )}
                      </Descriptions.Item>
                      {sub.tapTransactionId && (
                        <Descriptions.Item label={t("Last payment id")}>
                          {sub.tapTransactionId}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Col>
                  {/* If you re-enable usage, the labels already use t("...") */}
                </Row>
              </Card>

              <Card
                style={{ marginTop: 16, borderRadius: 12 }}
                title={
                  <Space>
                    <ThunderboltOutlined /> {t("Suggestions")}
                  </Space>
                }
              >
                {!suggestions?.length ? (
                  <Empty description={t("No suggestions right now")} />
                ) : (
                  <List
                    grid={{ gutter: 16, xs: 1, sm: 2, lg: 3 }}
                    dataSource={suggestions}
                    renderItem={(sugg) => (
                      <List.Item>
                        <Card hoverable style={{ borderRadius: 12 }}>
                          <Space direction="vertical" style={{ width: "100%" }}>
                            <Space
                              style={{
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <Text strong>{sugg.name}</Text>
                              <Tag color="blue">
                                {prettyMoney(sugg?.price?.discounted)}
                              </Tag>
                            </Space>
                            <Space wrap>
                              <LimitPill
                                label="Products"
                                value={sugg?.limits?.productLimit}
                              />
                              <LimitPill
                                label="Users"
                                value={sugg?.limits?.userAccessLimit}
                              />
                            </Space>
                            <Text type="secondary">{sugg.reason}</Text>
                            <Button
                              type="primary"
                              icon={<ArrowRightOutlined />}
                              onClick={() => goUpgradeFromSuggestion(sugg)}
                            >
                              {t("Upgrade")}
                            </Button>
                          </Space>
                        </Card>
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                title={
                  <Space>
                    <SyncOutlined /> {t("Actions")}
                  </Space>
                }
                style={{ borderRadius: 12 }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Button type="primary" block onClick={openUpgradeDrawer}>
                    {t("Change / Upgrade Package")}
                  </Button>
                  <Button block onClick={load}>
                    {t("Refresh")}
                  </Button>
                </Space>
                <Divider />
                <Alert
                  showIcon
                  type="info"
                  message={t("Tip")}
                  description={t(
                    "Upgrades take effect immediately and a new billing cycle starts today."
                  )}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Drawer
        title={t("Choose a POS + Inventory package")}
        width={drawerWidth}
        open={pkgDrawerOpen}
        onClose={() => setPkgDrawerOpen(false)}
        extra={
          <Space wrap>
            <Segmented
              options={[
                { label: t("All"), value: "All" },
                { label: t("yearly"), value: "yearly" },
              ]}
              value={intervalFilter}
              onChange={setIntervalFilter}
            />
            <Segmented
              options={[
                { label: t("All"), value: "All" },
                { label: t("Basic"), value: "Basic" },
                { label: t("Starter"), value: "Starter" },
                { label: t("Professional"), value: "Professional" },
                { label: t("Business"), value: "Business" },
              ]}
              value={planFilter}
              onChange={setPlanFilter}
            />
            <Input.Search
              allowClear
              placeholder={t("Search packages")}
              onSearch={setSearch}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: screens.xs ? 200 : 260 }}
            />
          </Space>
        }
      >
        <Table
          rowKey={(r) => r.id}
          columns={pkgColumns}
          dataSource={filteredRows}
          pagination={{ pageSize: 8 }}
          scroll={{ x: 720 }}
        />
      </Drawer>
    </div>
  );
}
